import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import { DotText } from "@/v2-backoffice-client/components/dot"
import Table, {
  createColumns,
  GROUPING_RANGE_DATES,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import CustomerUserTableRowToolbar from "@/v2-backoffice-client/customer/user/CustomerUserTableRowToolbar"
import CustomerUserTableSelectionToolbar from "@/v2-backoffice-client/customer/user/CustomerUserTableSelectionToolbar"
import UserActivityBadge from "@/v2-backoffice-client/user/activity/UserActivityBadge"
import UserStatusBadge from "@/v2-backoffice-client/user/status/UserStatusBadge"
import UserContactSyncCell from "@/v2-backoffice-client/user/contact/UserContactSyncCell"
import {
  CONTACT_SYNC_STATUS_CLIENT_FILTER_OPTIONS,
  getContactSyncStatusLabel,
} from "@/v2-backoffice-client/user/contact/userContact.constants"
import UserMigrationCell from "@/v2-backoffice-client/user/migration/UserMigrationCell"
import {
  ACTIVATION_STATUS_CLIENT_FILTER_OPTIONS,
  getActivationStatusLabel,
} from "@/v2-backoffice-client/tenant/migration/tenantMigration.constants"
import TruncatedList from "@/v2-backoffice-client/components/truncatedList"
import { buildUserUsageColumns } from "@/v2-backoffice-client/usage/usage.columns"
import {
  buildUserLicenseColumns,
  buildUserSeatsColumn,
} from "@/v2-backoffice-client/user/user.columns"
import {
  useLicenseGoverningStatuses,
  type LicenseStatusFilterOption,
} from "@/v2-backoffice-client/license/license.statuses.hooks"
import {
  groupSeatProductsByUser,
  type LicenseSeatHolder,
} from "@/v2-backoffice-client/license/seats/license.seats.utils"

const ACTIVE_STATUS_CODE = 0
const DELETED_STATUS_CODE = 1

const COLUMN_SIZING = {
  UserStatusCode: 90,
  UserActivityLevelCode: 80,
  ContactSyncStatus: 120,
  MigrationStatus: 130,
  TenantNames: 150,
  IdentityCreatedAt: 130,
  LastSignInAt: 130,
  CreatedAt: 130,
}

function createCustomerUserColumns(input: {
  /** Null when nobody at the customer holds a seat: the column is left out. */
  seatProductsByUserId: Map<number, string[]> | null
  licenseStatusNamesByCode: Map<number, string>
  /** As labels: this table filters client-side against the label. */
  licenseStatusOptions: LicenseStatusFilterOption[]
}) {
  const {
    seatProductsByUserId,
    licenseStatusNamesByCode,
    licenseStatusOptions,
  } = input

  return createColumns<UserFragment>({
    enableSelection: true,
    enableSorting: true,
    enableHiding: true,
    columns: {
      Name: {
        id: "Name",
        label: "Name",
        getValue: (row) => formatUserName(row.FirstName, row.LastName),
        to: ({ row }) => `>>?v=user:${row.original.Id}`,
      },
      UserStatusCode: {
        id: "UserStatusCode",
        label: "Status",
        enableGrouping: true,
        renderCell({ row }) {
          return <UserStatusBadge status={row.original.UserStatus} />
        },
        exportValue({ row }) {
          return row.original.UserStatus.Name
        },
      },
      UserActivityLevelCode: {
        id: "UserActivityLevelCode",
        label: "Activity",
        enableGrouping: true,
        filter: {
          type: "select",
          options: [
            { label: "Daily", value: "1" },
            { label: "Weekly", value: "2" },
            { label: "Monthly", value: "3" },
            { label: "Inactive", value: "4" },
            { label: "Unknown", value: "5" },
          ],
        },
        renderCell({ row }) {
          return <UserActivityBadge level={row.original.UserActivityLevel} />
        },
        exportValue({ row }) {
          return row.original.UserActivityLevel.Name
        },
      },
      Email: {
        id: "Email",
        label: "Email",
      },
      // A customer's users are exactly the slice sales cares about, so
      // surface the HubSpot link state (badge tooltip carries the "Sync
      // to HubSpot" repair action).
      ContactSyncStatus: {
        id: "ContactSyncStatus",
        label: "HubSpot",
        enableGrouping: true,
        // Client-side filtering compares against getValue — the label.
        filter: {
          type: "multi-select",
          placeholder: "Any HubSpot status",
          options: CONTACT_SYNC_STATUS_CLIENT_FILTER_OPTIONS,
        },
        getValue: (row) => getContactSyncStatusLabel(row.ContactSyncStatus),
        renderCell({ row }) {
          return <UserContactSyncCell user={row.original} showHealthy />
        },
        exportValue({ row }) {
          return getContactSyncStatusLabel(row.original.ContactSyncStatus)
        },
      },
      // Fusion-login migration state (FUS-927): the per-customer view CS
      // works from during the switchover, so it mirrors the Users board.
      // The FUS-927 migration target group: who of this customer's users
      // has actually used v2. Client-filtered against the label.
      UsesV2: {
        id: "UsesV2",
        label: "V2 User",
        enableGrouping: true,
        filter: {
          type: "select",
          placeholder: "V2 usage",
          options: [
            { label: "V2 user", value: "Yes" },
            { label: "Never used v2", value: "No" },
          ],
        },
        getValue: (row) => (row.UsesV2 ? "Yes" : "No"),
        renderCell({ row }) {
          return (
            <DotText variant={row.original.UsesV2 ? "success-1" : "muted"}>
              {row.original.UsesV2 ? "Yes" : "No"}
            </DotText>
          )
        },
        exportValue({ row }) {
          return row.original.UsesV2 ? "Yes" : "No"
        },
      },
      MigrationStatus: {
        id: "MigrationStatus",
        label: "Fusion Login",
        enableGrouping: true,
        // Client-side filtering compares against getValue — the label.
        filter: {
          type: "multi-select",
          placeholder: "Any migration status",
          options: ACTIVATION_STATUS_CLIENT_FILTER_OPTIONS,
        },
        getValue: (row) => getActivationStatusLabel(row.MigrationStatus),
        renderCell({ row }) {
          return <UserMigrationCell user={row.original} />
        },
        exportValue({ row }) {
          return getActivationStatusLabel(row.original.MigrationStatus)
        },
      },
      TenantNames: {
        id: "TenantNames",
        label: "Team(s)",
        enableGrouping: true,
        getValue: (row) => row.TenantNames?.[0] ?? "",
        renderCell({ row }) {
          return (
            <TruncatedList
              items={row.original.Tenants.map((tenant) => ({
                label: tenant.Name,
                to: `>>?v=tenant:${tenant.Id}`,
              }))}
            />
          )
        },
        exportValue({ row }) {
          return row.original.TenantNames?.join(", ") ?? ""
        },
      },
      // The owning customer's license posture, the same three columns as
      // the Users board. Every user this tab lists is owned by this
      // customer, so the rows agree; they are here so a user row reads the
      // same wherever it is listed, and for the export.
      ...buildUserLicenseColumns<UserFragment>({
        getUser: (row) => row,
        statusNamesByCode: licenseStatusNamesByCode,
        statusOptions: licenseStatusOptions,
      }),
      // The seats this person holds on the customer's seated licenses (the
      // MCP servers), by product: a seat is what lets them use that
      // license. Present only while someone at the customer holds one, so
      // a customer without a seated license does not carry an empty column.
      ...(seatProductsByUserId &&
        buildUserSeatsColumn<UserFragment>({
          getUserId: (row) => row.Id,
          seatProductsByUserId,
        })),
      // Same usage signals as the top-level Users board; the deep link
      // stacks the user panel open on its Usage tab.
      ...buildUserUsageColumns<UserFragment>({
        getUsage: (row) => row,
        buildTo: (row) => `>>?v=user:${row.Id}&user-view-tabs-tab=usage`,
      }),
      IdentityCreatedAt: {
        id: "IdentityCreatedAt",
        label: "Sign-up",
        defaultHidden: true,
        renderCell({ row }) {
          return <RelativeDate date={row.original.IdentityCreatedAt} />
        },
        exportValue({ row }) {
          return formatDateString(row.original.IdentityCreatedAt)
        },
      },
      LastSignInAt: {
        id: "LastSignInAt",
        label: "Last Sign-in",
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          return <RelativeDate date={row.original.LastSignInAt} />
        },
        exportValue({ row }) {
          return formatDateString(row.original.LastSignInAt)
        },
      },
      CreatedAt: {
        id: "CreatedAt",
        label: "Created",
        defaultHidden: true,
        renderCell({ row }) {
          return <RelativeDate date={row.original.CreatedAt} />
        },
        exportValue({ row }) {
          return formatDateString(row.original.CreatedAt)
        },
      },
    },
  })
}

export type CustomerUserTableProps = Omit<
  TableProps<UserFragment>,
  "data" | "columns"
> & {
  users: UserFragment[]
  isLoading?: boolean
  error?: Error
  activeCount?: number
  removedCount?: number
  /** Seats held across the customer's seated licenses, for the Seats column. */
  seatHolders?: LicenseSeatHolder[]
  onDeletedFilterChange?(onlyDeleted: boolean | undefined): void
  onRefresh?(): void
}

function CustomerUserTable(props: CustomerUserTableProps) {
  const {
    className,
    users,
    isLoading,
    error,
    activeCount,
    removedCount,
    seatHolders,
    onDeletedFilterChange,
    onRefresh,
    ...tableProps
  } = props

  const { namesByCode: licenseStatusNamesByCode, labelOptions } =
    useLicenseGoverningStatuses()

  const columns = useMemo(
    () =>
      createCustomerUserColumns({
        seatProductsByUserId:
          seatHolders && seatHolders.length > 0
            ? groupSeatProductsByUser(seatHolders)
            : null,
        licenseStatusNamesByCode,
        licenseStatusOptions: labelOptions,
      }),
    [seatHolders, licenseStatusNamesByCode, labelOptions],
  )

  return (
    <Table
      className={cn("customer-user-table", className)}
      tableId="customer-user-table"
      data={users}
      columns={columns}
      initialColumnSizing={COLUMN_SIZING}
      enableGrouping
      enableRowSelection
      {...tableProps}
    >
      <Table.Toolbar>
        <Table.StatusFilter
          defaultValue={ACTIVE_STATUS_CODE}
          onChange={(value) => {
            onDeletedFilterChange?.(
              value === null ? undefined : value === DELETED_STATUS_CODE,
            )
          }}
        >
          <Table.StatusFilter.Option
            value={null}
            count={
              activeCount !== undefined && removedCount !== undefined
                ? activeCount + removedCount
                : undefined
            }
          >
            All
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            variant="success"
            value={ACTIVE_STATUS_CODE}
            count={activeCount}
          >
            Active
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            variant="destructive"
            value={DELETED_STATUS_CODE}
            count={removedCount}
          >
            Removed
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />

        {/* Global search: name, email, team — standardized across every
            user-shaped table. */}
        <Table.Search placeholder="Search by name or email…" />
        <Table.ToolbarFilters />
        <Separator orientation="vertical" />
        <Table.ToolbarMenu
          onRefresh={onRefresh}
          exportFileName="customer-users"
        />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.RowToolbar<UserFragment> column="Name">
        {(user) => <CustomerUserTableRowToolbar user={user} />}
      </Table.RowToolbar>

      <Table.Content countLabel="users" isLoading={isLoading} error={error} />

      <Table.SelectionToolbar<UserFragment>>
        {(users, reset) => (
          <CustomerUserTableSelectionToolbar users={users} reset={reset} />
        )}
      </Table.SelectionToolbar>
    </Table>
  )
}

export default CustomerUserTable
